import { getUrl, sessionExpired } from '../setEnvironment';

class DmxAPI {
  constructor() {
    this.URL = getUrl();
    this.getControllers = this.getControllers.bind(this);
    this.createController = this.createController.bind(this);
    this.updateController = this.updateController.bind(this);
    this.toggleControllerStatus = this.toggleControllerStatus.bind(this);
    this.deleteController = this.deleteController.bind(this);
    this.getUniversesByController = this.getUniversesByController.bind(this);
    this.createUniverse = this.createUniverse.bind(this);
    this.deleteUniverse = this.deleteUniverse.bind(this);
    this.toggleUniverseStatus = this.toggleUniverseStatus.bind(this);
    this.getOccupancy = this.getOccupancy.bind(this);
    this.getDeviceById = this.getDeviceById.bind(this);
    this.createDevice = this.createDevice.bind(this);
    this.updateDevice = this.updateDevice.bind(this);
    this.deleteDevice = this.deleteDevice.bind(this);
    this.getProfiles = this.getProfiles.bind(this);
    this.getProfileById = this.getProfileById.bind(this);
    this.createProfile = this.createProfile.bind(this);
    this.updateProfile = this.updateProfile.bind(this);
    this.deleteProfile = this.deleteProfile.bind(this);
    this.setProfileChannels = this.setProfileChannels.bind(this);
    this.getDevicePresets = this.getDevicePresets.bind(this);
    this.getDevicePreset = this.getDevicePreset.bind(this);
    this.createDevicePreset = this.createDevicePreset.bind(this);
    this.updateDevicePreset = this.updateDevicePreset.bind(this);
    this.deleteDevicePreset = this.deleteDevicePreset.bind(this);
    this.activateDevicePreset = this.activateDevicePreset.bind(this);
    this.getCompatibleDevices = this.getCompatibleDevices.bind(this);
    this.duplicateDevicePresets = this.duplicateDevicePresets.bind(this);
  }

  async getControllers(token) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createController(token, controller) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ name: controller.name, ip: controller.ip }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async updateController(token, controllerId, controller) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers/' + controllerId, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ name: controller.name, ip: controller.ip }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async toggleControllerStatus(token, controllerId, active) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers/' + controllerId + '/status', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ active }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async deleteController(token, controllerId) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers/' + controllerId, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async getUniversesByController(token, controllerId) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers/' + controllerId + '/universes', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createUniverse(token, controllerId, universe) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers/' + controllerId + '/universes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          universe_number: universe.universe_number,
          controller_universe_number: universe.controller_universe_number,
          channel_count: universe.channel_count,
        }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async deleteUniverse(token, controllerId, universeId) {
    try {
      const response = await fetch(this.URL + '/dmx/controllers/' + controllerId + '/universes/' + universeId, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async toggleUniverseStatus(token, universeId, active) {
    try {
      const response = await fetch(this.URL + '/dmx/universes/' + universeId + '/status', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ active }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async getOccupancy(token, universeId) {
    try {
      const response = await fetch(this.URL + '/dmx/universes/' + universeId + '/occupancy', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getDeviceById(token, deviceId) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createDevice(token, device) {
    try {
      const response = await fetch(this.URL + '/dmx/devices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          name: device.name,
          device_type: device.device_type,
          linked_entity_type: device.linked_entity_type,
          linked_entity_id: device.linked_entity_id,
          controller_id: device.controller_id,
          universe_id: device.universe_id,
          start_channel: device.start_channel,
          channel_count: device.channel_count,
          profile_id: device.profile_id,
        }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async updateDevice(token, deviceId, device) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          name: device.name,
          device_type: device.device_type,
          linked_entity_type: device.linked_entity_type,
          linked_entity_id: device.linked_entity_id,
          controller_id: device.controller_id,
          universe_id: device.universe_id,
          start_channel: device.start_channel,
          channel_count: device.channel_count,
          active: device.active,
          profile_id: device.profile_id,
        }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async deleteDevice(token, deviceId) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }
  async getProfiles(token, deviceType) {
    try {
      const query = deviceType ? '?device_type=' + deviceType : '';
      const response = await fetch(this.URL + '/dmx/profiles' + query, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getProfileById(token, profileId) {
    try {
      const response = await fetch(this.URL + '/dmx/profiles/' + profileId, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createProfile(token, profile) {
    try {
      const response = await fetch(this.URL + '/dmx/profiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          name: profile.name,
          device_type: profile.device_type,
          channel_count: profile.channel_count,
        }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async updateProfile(token, profileId, profile) {
    try {
      const response = await fetch(this.URL + '/dmx/profiles/' + profileId, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({
          name: profile.name,
          device_type: profile.device_type,
          channel_count: profile.channel_count,
        }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async deleteProfile(token, profileId) {
    try {
      const response = await fetch(this.URL + '/dmx/profiles/' + profileId, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async setProfileChannels(token, profileId, channels) {
    try {
      const response = await fetch(this.URL + '/dmx/profiles/' + profileId + '/channels', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ channels }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async getDevicePresets(token, deviceId) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/presets', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async getDevicePreset(token, deviceId, presetId) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/presets/' + presetId, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async createDevicePreset(token, deviceId, preset) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ name: preset.name, values: preset.values }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async updateDevicePreset(token, deviceId, presetId, preset) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/presets/' + presetId, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ name: preset.name, values: preset.values }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async deleteDevicePreset(token, deviceId, presetId) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/presets/' + presetId, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async activateDevicePreset(token, deviceId, presetId) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/presets/' + presetId + '/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({}),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }

  async getCompatibleDevices(token, deviceId) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/compatible-devices', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', Authorization: token },
      });
      if (sessionExpired(response.status)) return;
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  async duplicateDevicePresets(token, deviceId, targetDeviceIds) {
    try {
      const response = await fetch(this.URL + '/dmx/devices/' + deviceId + '/presets/duplicate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ targetDeviceIds }),
      });
      if (sessionExpired(response.status)) return;
      const json = await response.json();
      if (!response.ok) throw new Error(json.message);
      return json;
    } catch (error) {
      throw error;
    }
  }
}

let _dmxAPI = null;

const initDmxAPI = () => {
  if (!_dmxAPI) {
    _dmxAPI = new DmxAPI();
  }
  return _dmxAPI;
};

const getDmxAPI = () => {
  return initDmxAPI();
};

export { initDmxAPI, getDmxAPI };
